import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  CardHeader,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Divider,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Alert,
  Stack,
  Avatar,
  Fade,
  Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  LocalDining as MealIcon,
  DirectionsCar as TransportIcon,
  Work as UniformIcon,
  Build as EquipmentIcon,
  AccountBalanceWallet as LoanIcon,
  MoreHoriz as OtherIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { deductionService, EmployeeDeduction } from '../services/deductionService';
import { employeeService } from '../services/employeeService';
import { Employee } from '../types';

interface EmployeeDeductionsProps {
  employeeId?: string;
  onClose?: () => void;
}

const EmployeeDeductions: React.FC<EmployeeDeductionsProps> = ({ employeeId, onClose }) => {
  const [deductions, setDeductions] = useState<EmployeeDeduction[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(employeeId || '');
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDeduction, setNewDeduction] = useState({
    employee_id: employeeId || '',
    deduction_type: 'cash_advance' as const,
    description: '',
    amount: 0,
    installment_amount: 0,
    start_date: new Date().toISOString().split('T')[0],
    is_recurring: true,
    is_active: true,
    notes: ''
  });

  useEffect(() => {
    loadEmployees();
    if (selectedEmployeeId) {
      loadDeductions(selectedEmployeeId);
    }
  }, [selectedEmployeeId]);

  const loadEmployees = async () => {
    try {
      const employeeData = await employeeService.getAll();
      setEmployees(employeeData.filter(emp => emp.status === 'active'));
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadDeductions = async (empId: string) => {
    if (!empId) return;
    
    setLoading(true);
    try {
      const deductionData = await deductionService.getEmployeeDeductions(empId);
      setDeductions(deductionData);
    } catch (error) {
      console.error('Error loading deductions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDeduction = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmployeeId) return;

    try {
      const deductionData = {
        ...newDeduction,
        employee_id: selectedEmployeeId,
        remaining_balance: newDeduction.amount
      };
      
      await deductionService.createDeduction(deductionData);
      await loadDeductions(selectedEmployeeId);
      setShowAddForm(false);
      setNewDeduction({
        employee_id: selectedEmployeeId,
        deduction_type: 'cash_advance',
        description: '',
        amount: 0,
        installment_amount: 0,
        start_date: new Date().toISOString().split('T')[0],
        is_recurring: true,
        is_active: true,
        notes: ''
      });
    } catch (error) {
      console.error('Error adding deduction:', error);
      alert('Failed to add deduction. Please try again.');
    }
  };

  const handleDeleteDeduction = async (deductionId: string) => {
    if (!window.confirm('Are you sure you want to delete this deduction?')) return;

    try {
      await deductionService.deleteDeduction(deductionId);
      await loadDeductions(selectedEmployeeId);
    } catch (error) {
      console.error('Error deleting deduction:', error);
      alert('Failed to delete deduction. Please try again.');
    }
  };

  const selectedEmployee = employees.find(emp => emp.id === selectedEmployeeId);
  const totalActiveDeductions = deductions.reduce((sum, d) => sum + d.remaining_balance, 0);

  const getDeductionIcon = (type: string) => {
    switch (type) {
      case 'cash_advance': return <MoneyIcon />;
      case 'meal': return <MealIcon />;
      case 'transportation': return <TransportIcon />;
      case 'uniform': return <UniformIcon />;
      case 'equipment': return <EquipmentIcon />;
      case 'loan': return <LoanIcon />;
      default: return <OtherIcon />;
    }
  };

  const getDeductionColor = (type: string) => {
    switch (type) {
      case 'cash_advance': return 'primary';
      case 'meal': return 'success';
      case 'transportation': return 'info';
      case 'uniform': return 'warning';
      case 'equipment': return 'secondary';
      case 'loan': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>

        {/* Employee Selection */}
        {!employeeId && (
          <Card elevation={2}>
            <CardContent>
              <FormControl fullWidth>
                <InputLabel>Select Employee</InputLabel>
                <Select
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  label="Select Employee"
                >
                  <MenuItem value="">Choose an employee...</MenuItem>
                  {employees.map(employee => (
                    <MenuItem key={employee.id} value={employee.id}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                          {employee.first_name[0]}{employee.last_name[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {employee.first_name} {employee.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {employee.department}
                          </Typography>
                        </Box>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        )}

        {selectedEmployeeId && selectedEmployee && (
          <>
            {/* Employee Info */}
            <Card elevation={3} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar 
                    sx={{ 
                      width: 56, 
                      height: 56, 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontSize: '1.5rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {selectedEmployee.first_name[0]}{selectedEmployee.last_name[0]}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                      {selectedEmployee.first_name} {selectedEmployee.last_name}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                      {selectedEmployee.department} • {selectedEmployee.position}
                    </Typography>
                  </Box>
                </Stack>
                <Divider sx={{ bgcolor: 'rgba(255,255,255,0.2)', my: 2 }} />
                <Stack direction="row" spacing={2} alignItems="center">
                  <MoneyIcon sx={{ color: 'rgba(255,255,255,0.9)' }} />
                  <Typography variant="h6" sx={{ color: 'white' }}>
                    Total Active Deductions: ₱{totalActiveDeductions.toFixed(2)}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>

            {/* Add Deduction Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant={showAddForm ? "outlined" : "contained"}
                color={showAddForm ? "secondary" : "primary"}
                startIcon={showAddForm ? <ExpandLessIcon /> : <AddIcon />}
                onClick={() => setShowAddForm(!showAddForm)}
                size="large"
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 'medium'
                }}
              >
                {showAddForm ? 'Cancel' : 'Add New Deduction'}
              </Button>
            </Box>

            {/* Add Deduction Form */}
            <Collapse in={showAddForm}>
              <Card elevation={2} sx={{ mt: 2 }}>
                <CardHeader 
                  title="Add New Deduction" 
                  avatar={<AddIcon color="primary" />}
                  sx={{ bgcolor: 'grey.50' }}
                />
                <CardContent>
                  <Box component="form" onSubmit={handleAddDeduction}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                       <FormControl fullWidth required>
                         <InputLabel>Deduction Type</InputLabel>
                         <Select
                           value={newDeduction.deduction_type}
                           onChange={(e) => setNewDeduction({...newDeduction, deduction_type: e.target.value as any})}
                           label="Deduction Type"
                         >
                           <MenuItem value="cash_advance">
                             <Stack direction="row" spacing={1} alignItems="center">
                               <MoneyIcon fontSize="small" />
                               <span>Cash Advance</span>
                             </Stack>
                           </MenuItem>
                           <MenuItem value="meal">
                             <Stack direction="row" spacing={1} alignItems="center">
                               <MealIcon fontSize="small" />
                               <span>Meal</span>
                             </Stack>
                           </MenuItem>
                           <MenuItem value="transportation">
                             <Stack direction="row" spacing={1} alignItems="center">
                               <TransportIcon fontSize="small" />
                               <span>Transportation</span>
                             </Stack>
                           </MenuItem>
                           <MenuItem value="uniform">
                             <Stack direction="row" spacing={1} alignItems="center">
                               <UniformIcon fontSize="small" />
                               <span>Uniform</span>
                             </Stack>
                           </MenuItem>
                           <MenuItem value="equipment">
                             <Stack direction="row" spacing={1} alignItems="center">
                               <EquipmentIcon fontSize="small" />
                               <span>Equipment</span>
                             </Stack>
                           </MenuItem>
                           <MenuItem value="loan">
                             <Stack direction="row" spacing={1} alignItems="center">
                               <LoanIcon fontSize="small" />
                               <span>Loan</span>
                             </Stack>
                           </MenuItem>
                           <MenuItem value="other">
                             <Stack direction="row" spacing={1} alignItems="center">
                               <OtherIcon fontSize="small" />
                               <span>Other</span>
                             </Stack>
                           </MenuItem>
                         </Select>
                       </FormControl>
                       <TextField
                         fullWidth
                         label="Description"
                         value={newDeduction.description}
                         onChange={(e) => setNewDeduction({...newDeduction, description: e.target.value})}
                         placeholder="Brief description"
                       />
                       <TextField
                         fullWidth
                         label="Total Amount (₱)"
                         type="number"
                         inputProps={{ step: 0.01, min: 0 }}
                         value={newDeduction.amount === 0 ? '' : newDeduction.amount}
                         onChange={(e) => {
                           const value = e.target.value;
                           setNewDeduction({...newDeduction, amount: value === '' ? 0 : parseFloat(value)});
                         }}
                         required
                       />
                       <TextField
                         fullWidth
                         label="Installment Amount (₱)"
                         type="number"
                         inputProps={{ step: 0.01, min: 0 }}
                         value={newDeduction.installment_amount === 0 ? '' : newDeduction.installment_amount}
                         onChange={(e) => {
                           const value = e.target.value;
                           setNewDeduction({...newDeduction, installment_amount: value === '' ? 0 : parseFloat(value)});
                         }}
                         placeholder="Monthly deduction amount"
                       />
                       <TextField
                         fullWidth
                         label="Start Date"
                         type="date"
                         value={newDeduction.start_date}
                         onChange={(e) => setNewDeduction({...newDeduction, start_date: e.target.value})}
                         InputLabelProps={{ shrink: true }}
                         required
                       />
                       <FormControlLabel
                         control={
                           <Checkbox
                             checked={newDeduction.is_recurring}
                             onChange={(e) => setNewDeduction({...newDeduction, is_recurring: e.target.checked})}
                             color="primary"
                           />
                         }
                         label="Recurring Deduction"
                       />
                     </Box>
                     <TextField
                       fullWidth
                       label="Notes"
                       multiline
                       rows={3}
                       value={newDeduction.notes}
                       onChange={(e) => setNewDeduction({...newDeduction, notes: e.target.value})}
                       placeholder="Additional notes..."
                       sx={{ mt: 3 }}
                     />
                    <Stack direction="row" spacing={2} sx={{ mt: 3, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={() => setShowAddForm(false)}
                        sx={{ textTransform: 'none' }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<AddIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        Add Deduction
                      </Button>
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Collapse>

            {/* Deductions List */}
            <Card elevation={2}>
              <CardHeader 
                title="Active Deductions" 
                avatar={<ReceiptIcon color="primary" />}
                sx={{ bgcolor: 'grey.50' }}
              />
              <CardContent>
                {loading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Loading deductions...
                    </Typography>
                  </Box>
                ) : deductions.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <ReceiptIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No active deductions found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This employee has no active deductions.
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={2}>
                    {deductions.map(deduction => (
                      <Card key={deduction.id} variant="outlined" sx={{ transition: 'all 0.2s', '&:hover': { boxShadow: 2 } }}>
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box sx={{ flex: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                <Avatar 
                                  sx={{ 
                                    width: 32, 
                                    height: 32, 
                                    bgcolor: `${getDeductionColor(deduction.deduction_type)}.main`,
                                    color: 'white'
                                  }}
                                >
                                  {getDeductionIcon(deduction.deduction_type)}
                                </Avatar>
                                <Stack direction="row" spacing={1}>
                                  <Chip 
                                    label={deduction.deduction_type.replace('_', ' ').toUpperCase()}
                                    color={getDeductionColor(deduction.deduction_type) as any}
                                    size="small"
                                    variant="filled"
                                  />
                                  {deduction.is_recurring && (
                                    <Chip 
                                      label="RECURRING"
                                      color="success"
                                      size="small"
                                      variant="outlined"
                                      icon={<ScheduleIcon />}
                                    />
                                  )}
                                </Stack>
                              </Stack>
                              <Typography variant="h6" gutterBottom>
                                {deduction.description || deduction.deduction_type.replace('_', ' ')}
                              </Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mt: 1 }}>
                                 <Box>
                                   <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                                   <Typography variant="body2" fontWeight="medium">₱{deduction.amount.toFixed(2)}</Typography>
                                 </Box>
                                 <Box>
                                   <Typography variant="caption" color="text.secondary">Remaining</Typography>
                                   <Typography variant="body2" fontWeight="medium" color="error.main">
                                     ₱{deduction.remaining_balance.toFixed(2)}
                                   </Typography>
                                 </Box>
                                 <Box>
                                   <Typography variant="caption" color="text.secondary">Installment</Typography>
                                   <Typography variant="body2" fontWeight="medium">₱{(deduction.installment_amount || 0).toFixed(2)}</Typography>
                                 </Box>
                                 <Box>
                                   <Typography variant="caption" color="text.secondary">Start Date</Typography>
                                   <Typography variant="body2" fontWeight="medium">
                                     {new Date(deduction.start_date).toLocaleDateString()}
                                   </Typography>
                                 </Box>
                               </Box>
                              {deduction.notes && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                  <Typography variant="body2">{deduction.notes}</Typography>
                                </Alert>
                              )}
                            </Box>
                            <IconButton
                              onClick={() => handleDeleteDeduction(deduction.id)}
                              color="error"
                              sx={{ ml: 2 }}
                              title="Delete deduction"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default EmployeeDeductions;